// Where the employee lives (their US state, or Canada) decides which policies answer them, and HR
// wants it on record as state laws change. Ask HR asks once, before their first answer. The choice
// is stored and used on the web and in Slack; they can change it any time in Settings.

import { useEffect, useState } from "react";
import { api } from "../api.js";
import { US_STATES, type LocationSummary, type WorkLocation } from "@ask-hr/core/sources";

const STATES = Object.entries(US_STATES).sort((a, b) => a[1].localeCompare(b[1]));

const keyOf = (l: WorkLocation) => (l.country === "CA" ? "CA" : `US-${l.state}`);
const fromKey = (k: string): WorkLocation => (k === "CA" ? { country: "CA" } : { country: "US", state: k.slice(3) });

export function LocationPicker({ location, onChoose }: { location: LocationSummary; onChoose: (l: WorkLocation) => void }) {
  const chosen = location.source === "chosen" ? location.place : null;
  return (
    <>
      <label className="sr-only" htmlFor="work-location">
        Where you live
      </label>
      <select id="work-location" value={chosen ? keyOf(chosen) : ""} onChange={(e) => e.target.value && onChoose(fromKey(e.target.value))}>
        {chosen ? null : (
          <option value="" disabled>
            Not set
          </option>
        )}
        <option value="CA">Canada</option>
        <optgroup label="United States">
          {STATES.map(([code, name]) => (
            <option key={code} value={`US-${code}`}>
              {name}
            </option>
          ))}
        </optgroup>
      </select>
    </>
  );
}

// Settings > Where you live. Saved to their account (not this browser), so it applies on the web
// and in Slack.
export function LocationSetting() {
  const [location, setLocation] = useState<LocationSummary | null>(null);
  const [status, setStatus] = useState<"" | "saved" | "error">("");
  useEffect(() => {
    let live = true;
    api<{ location: LocationSummary }>("GET", "/api/me").then(
      (r) => live && setLocation(r.location),
      () => live && setStatus("error"),
    );
    return () => {
      live = false;
    };
  }, []);
  async function choose(place: WorkLocation) {
    try {
      setLocation((await api<{ location: LocationSummary }>("PUT", "/api/me/location", { place })).location);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }
  return (
    <div className="set-row">
      <span className="lbl">
        Where you live<small>Decides which policies answer you, on the web and in Slack</small>
      </span>
      <span>
        {location ? <LocationPicker location={location} onChoose={(l) => void choose(l)} /> : status === "error" ? null : "Loading…"}
        <span role="status" className="muted small">
          {status === "saved" ? " Saved" : status === "error" ? " Couldn't load or save your location. Try again." : ""}
        </span>
      </span>
    </div>
  );
}


// Asked once, before the first answer: United States or Canada, then (US) they type to find their
// state, and choosing a match is the answer. Nothing is suggested before they type: HR wants the
// state from them, not from the HR record. Typing a state into the question box works too
// (ChatPage, parsePlace).
export function LocationAsk({ onChoose }: { onChoose: (l: WorkLocation) => void }) {
  const [step, setStep] = useState<"country" | "state">("country");
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const matches = q
    ? STATES.filter(([code, name]) => name.toLowerCase().startsWith(q) || code.toLowerCase() === q)
        .concat(STATES.filter(([, name]) => !name.toLowerCase().startsWith(q) && name.toLowerCase().includes(q)))
        .slice(0, 6)
    : [];

  return (
    <div className="location-ask" role="group" aria-labelledby="where-h">
      <p id="where-h">
        <strong>Where do you live?</strong> Some policies and state laws depend on where you live, so this decides which ones
        answer you. You'll only be asked once.
      </p>
      {step === "country" ? (
        <div className="actions start">
          <button type="button" onClick={() => setStep("state")}>
            United States
          </button>
          <button type="button" onClick={() => onChoose({ country: "CA" })}>
            Canada
          </button>
        </div>
      ) : (
        <>
          <form
            className="where-find"
            onSubmit={(e) => {
              e.preventDefault();
              const first = matches[0];
              if (first) onChoose({ country: "US", state: first[0] });
            }}
          >
            <label htmlFor="where-state">Which state?</label>
            <input
              id="where-state"
              type="text"
              autoComplete="off"
              placeholder="Start typing your state"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-describedby="where-matches"
              autoFocus
            />
            <button type="button" onClick={() => setStep("country")}>
              Back
            </button>
          </form>
          <div id="where-matches" className="actions start" aria-live="polite">
            {q && matches.length === 0 ? <span className="muted small">No state matches “{query.trim()}”.</span> : null}
            {matches.map(([code, name]) => (
              <button key={code} type="button" onClick={() => onChoose({ country: "US", state: code })}>
                {name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
